import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Bot,
  MessageSquare,
  ToggleLeft,
  Smartphone,
  Trash2,
  Save,
  RefreshCw,
  AlertTriangle,
  Check,
  Globe,
  Unplug,
  Loader2,
  X,
  QrCode,
  AlertCircle,
  CheckCircle,
  Cpu,
  ChevronDown,
  Users,
  UserCheck,
  Power,
  PlayCircle,
  PauseCircle,
  Phone,
  Mail,
  MessageCircle,
  Calendar,
  Clock,
  Eye,
  EyeOff,
  ExternalLink,
  Send,
  Sparkles,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import Toggle from "../../components/Toggle";
import api from "../../services/api";
import { llmModels, languages as LANGUAGES } from "../../data/dummyData";

export default function Settings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateAgent } = useApp();

  // Get shared agent state from layout - this syncs with sidebar
  const { agent, setAgent } = useOutletContext();

  // Scroll to hash section (e.g., #whatsapp-section from email link)
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        // Small delay to ensure page is rendered
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location.hash]);

  const [formData, setFormData] = useState({
    name: "",
    businessType: "",
    description: "",
    language: "",
    model: "",
    behaviorPrompt: "",
    respondTo: "all",
    whitelist: [],
    features: {
      autoReply: true,
      leadCapture: true,
      appointments: false,
      gmailIntegration: false,
      humanHandoff: false,
    },
    handoffConfig: {
      phone: "",
      email: "",
      message: "",
    },
  });

  // Whitelist input state
  const [whitelistInput, setWhitelistInput] = useState("");

  const [hasChanges, setHasChanges] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Section-specific save states
  const [sectionSaving, setSectionSaving] = useState({});
  const [sectionSaved, setSectionSaved] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  // AI formatting states
  const [isFormattingDescription, setIsFormattingDescription] = useState(false);
  const [isFormattingBehavior, setIsFormattingBehavior] = useState(false);

  // WhatsApp connection modal state
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectPhoneNumber, setConnectPhoneNumber] = useState("");
  const [connectStep, setConnectStep] = useState("phone"); // 'phone', 'qr', 'connected'
  const [connectError, setConnectError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [qrCountdown, setQrCountdown] = useState(60);
  const [showScanInstructions, setShowScanInstructions] = useState(false);
  const countdownIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Human Handoff config modal state
  const [showHandoffModal, setShowHandoffModal] = useState(false);
  const [handoffConfigTemp, setHandoffConfigTemp] = useState({
    phone: "",
    email: "",
    message: "",
  });
  const [handoffError, setHandoffError] = useState(null);

  // Appointment Booking config modal state
  const defaultBookingConfig = {
    timezone: "Africa/Nairobi",
    slot_duration_minutes: 30,
    buffer_minutes: 15,
    min_advance_hours: 1,
    max_advance_days: 30,
    working_hours: {
      0: null,
      1: { start: "09:00", end: "17:00" },
      2: { start: "09:00", end: "17:00" },
      3: { start: "09:00", end: "17:00" },
      4: { start: "09:00", end: "17:00" },
      5: { start: "09:00", end: "17:00" },
      6: null,
    },
    breaks: [],
    confirmation_message: "",
  };
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingConfigTemp, setBookingConfigTemp] =
    useState(defaultBookingConfig);
  const [bookingError, setBookingError] = useState(null);
  const [openBookingDropdown, setOpenBookingDropdown] = useState(null);

  // Gmail Integration config modal state
  const defaultEmailConfig = {
    gmail_address: "",
    app_password: "",
    sender_name: "",
    signature: "",
  };
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailConfigTemp, setEmailConfigTemp] = useState(defaultEmailConfig);
  const [emailError, setEmailError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Dropdown options for booking modal
  const timezoneOptions = [
    { value: "Africa/Nairobi", label: "East Africa (Nairobi)" },
    { value: "Africa/Lagos", label: "West Africa (Lagos)" },
    { value: "Africa/Johannesburg", label: "South Africa (Johannesburg)" },
    { value: "Africa/Cairo", label: "Egypt (Cairo)" },
    { value: "Europe/London", label: "UK (London)" },
    { value: "Europe/Paris", label: "Central Europe (Paris)" },
    { value: "America/New_York", label: "US Eastern (New York)" },
    { value: "America/Los_Angeles", label: "US Pacific (Los Angeles)" },
    { value: "Asia/Dubai", label: "UAE (Dubai)" },
    { value: "Asia/Singapore", label: "Singapore" },
  ];

  const slotDurationOptions = [
    { value: 15, label: "15 minutes" },
    { value: 30, label: "30 minutes" },
    { value: 45, label: "45 minutes" },
    { value: 60, label: "1 hour" },
    { value: 90, label: "1.5 hours" },
    { value: 120, label: "2 hours" },
  ];

  const minAdvanceOptions = [
    { value: 1, label: "1 hour" },
    { value: 2, label: "2 hours" },
    { value: 4, label: "4 hours" },
    { value: 12, label: "12 hours" },
    { value: 24, label: "24 hours" },
    { value: 48, label: "48 hours" },
  ];

  const maxAdvanceOptions = [
    { value: 7, label: "1 week" },
    { value: 14, label: "2 weeks" },
    { value: 30, label: "1 month" },
    { value: 60, label: "2 months" },
    { value: 90, label: "3 months" },
  ];

  // Time options for working hours (30-min intervals)
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const value = `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? "AM" : "PM";
      const label = `${hour12}:${min.toString().padStart(2, "0")} ${ampm}`;
      timeOptions.push({ value, label });
    }
  }

  const formatTimeLabel = (time24) => {
    if (!time24) return "";
    const [hour, min] = time24.split(":").map(Number);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? "AM" : "PM";
    return `${hour12}:${min.toString().padStart(2, "0")} ${ampm}`;
  };

  // Initialize form data when agent changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || "",
        businessType: agent.businessType || agent.business_type || "",
        description: agent.description || "",
        language: agent.language || "English",
        model: agent.model || "openai/gpt-4o-mini",
        behaviorPrompt: agent.behaviorPrompt || agent.behavior || "",
        respondTo: agent.respond_to || agent.respondTo || "all",
        whitelist: agent.whitelist || [],
        features: agent.features || {
          autoReply: true,
          leadCapture: true,
          appointments: false,
          gmailIntegration: false,
          humanHandoff: false,
        },
        handoffConfig: agent.handoffConfig ||
          agent.handoff_config || {
            phone: "",
            email: "",
            message: "",
          },
        bookingConfig:
          agent.bookingConfig || agent.booking_config || defaultBookingConfig,
        emailConfig:
          agent.emailConfig || agent.email_config || defaultEmailConfig,
      });
    }
  }, [agent]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleFormatDescription = async () => {
    if (!formData.description.trim() || isFormattingDescription) return;

    setIsFormattingDescription(true);
    try {
      const response = await api.formatWithAI(
        formData.description,
        "business_description",
        { business_type: formData.businessType },
      );
      if (response.formatted_text) {
        handleChange("description", response.formatted_text);
      }
    } catch (err) {
      console.error("Failed to format description:", err);
    } finally {
      setIsFormattingDescription(false);
    }
  };

  const handleFormatBehavior = async () => {
    if (!formData.behaviorPrompt.trim() || isFormattingBehavior) return;

    setIsFormattingBehavior(true);
    try {
      const response = await api.formatWithAI(
        formData.behaviorPrompt,
        "behavior_prompt",
        {
          agent_name: formData.name,
          business_type: formData.businessType,
        },
      );
      if (response.formatted_text) {
        handleChange("behaviorPrompt", response.formatted_text);
      }
    } catch (err) {
      console.error("Failed to format behavior prompt:", err);
    } finally {
      setIsFormattingBehavior(false);
    }
  };

  const handleFeatureToggle = (feature) => {
    // Special handling for humanHandoff - show config modal when enabling
    if (feature === "humanHandoff" && !formData.features.humanHandoff) {
      // Initialize temp config with current values
      setHandoffConfigTemp({
        phone: formData.handoffConfig.phone || "",
        email: formData.handoffConfig.email || "",
        message: formData.handoffConfig.message || "",
      });
      setHandoffError(null);
      setShowHandoffModal(true);
      return;
    }

    // Special handling for appointmentBooking - show config modal when enabling
    if (
      feature === "appointmentBooking" &&
      !formData.features.appointmentBooking
    ) {
      setBookingConfigTemp({
        ...defaultBookingConfig,
        ...formData.bookingConfig,
      });
      setBookingError(null);
      setShowBookingModal(true);
      return;
    }

    // Special handling for gmailIntegration - show config modal when enabling
    if (feature === "gmailIntegration" && !formData.features.gmailIntegration) {
      setEmailConfigTemp({ ...defaultEmailConfig, ...formData.emailConfig });
      setEmailError(null);
      setTestEmailResult(null);
      setShowEmailModal(true);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, [feature]: !prev.features[feature] },
    }));
    setHasChanges(true);
  };

  // Handle saving handoff config from modal
  const handleSaveHandoffConfig = () => {
    // Validate at least one contact method is provided
    if (!handoffConfigTemp.phone.trim() && !handoffConfigTemp.email.trim()) {
      setHandoffError(
        "Please provide at least one contact method (phone or email)",
      );
      return;
    }

    // Save the config and enable the feature
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, humanHandoff: true },
      handoffConfig: {
        phone: handoffConfigTemp.phone.trim(),
        email: handoffConfigTemp.email.trim(),
        message: handoffConfigTemp.message.trim(),
      },
    }));
    setHasChanges(true);
    setShowHandoffModal(false);
  };

  // Handle closing handoff modal without saving
  const handleCloseHandoffModal = () => {
    setShowHandoffModal(false);
    setHandoffError(null);
  };

  // Booking config modal handlers
  const handleSaveBookingConfig = () => {
    const hasWorkingDay = Object.values(bookingConfigTemp.working_hours).some(
      (hours) => hours !== null,
    );
    if (!hasWorkingDay) {
      setBookingError("Please enable at least one working day");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, appointmentBooking: true },
      bookingConfig: { ...bookingConfigTemp },
    }));
    setHasChanges(true);
    setShowBookingModal(false);
  };

  const handleCloseBookingModal = () => {
    setShowBookingModal(false);
    setBookingError(null);
    setOpenBookingDropdown(null);
  };

  // Email modal handlers
  const handleSaveEmailConfig = async () => {
    console.log("DEBUG handleSaveEmailConfig called with:", emailConfigTemp);

    // Validate required fields
    if (!emailConfigTemp.gmail_address.trim()) {
      setEmailError("Please enter your Gmail address");
      return;
    }
    if (!emailConfigTemp.app_password.trim()) {
      setEmailError("Please enter your App Password");
      return;
    }
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailConfigTemp.gmail_address.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Save config and enable the feature
    const newEmailConfig = {
      gmail_address: emailConfigTemp.gmail_address.trim(),
      app_password: emailConfigTemp.app_password.trim(),
      sender_name: emailConfigTemp.sender_name.trim(),
      signature: emailConfigTemp.signature.trim(),
    };
    console.log(
      "DEBUG handleSaveEmailConfig: Setting emailConfig to:",
      newEmailConfig,
    );

    const newFeatures = { ...formData.features, gmailIntegration: true };

    // Update local state
    setFormData((prev) => ({
      ...prev,
      features: newFeatures,
      emailConfig: newEmailConfig,
    }));

    // Immediately save to backend (don't rely on state update)
    try {
      await api.updateAgentSettings(id, {
        features: newFeatures,
        email_config: newEmailConfig,
      });
      console.log("DEBUG: Email config saved to backend successfully");

      // Update contexts
      updateAgent(id, { features: newFeatures, emailConfig: newEmailConfig });
      setAgent((prev) => ({
        ...prev,
        features: newFeatures,
        email_config: newEmailConfig,
      }));
    } catch (err) {
      console.error("Failed to save email config:", err);
      setEmailError("Failed to save email configuration. Please try again.");
      return;
    }

    setHasChanges(false);
    setShowEmailModal(false);
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailError(null);
    setTestEmailResult(null);
    setShowPassword(false);
  };

  // Close booking dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openBookingDropdown && !event.target.closest(".booking-dropdown")) {
        setOpenBookingDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openBookingDropdown]);

  const toggleWorkingDay = (dayNum) => {
    setBookingConfigTemp((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayNum]: prev.working_hours[dayNum]
          ? null
          : { start: "09:00", end: "17:00" },
      },
    }));
  };

  const updateWorkingHours = (dayNum, field, value) => {
    setBookingConfigTemp((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [dayNum]: {
          ...prev.working_hours[dayNum],
          [field]: value,
        },
      },
    }));
  };

  // Whitelist handlers
  const handleAddWhitelistNumber = () => {
    if (whitelistInput.trim()) {
      handleChange("whitelist", [...formData.whitelist, whitelistInput.trim()]);
      setWhitelistInput("");
    }
  };

  const handleRemoveWhitelistNumber = (number) => {
    handleChange(
      "whitelist",
      formData.whitelist.filter((n) => n !== number),
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      // Debug: Log the email_config being sent
      console.log(
        "DEBUG handleSave: formData.emailConfig =",
        formData.emailConfig,
      );

      // Call the backend API to update the agent settings
      await api.updateAgentSettings(id, {
        name: formData.name,
        business_type: formData.businessType,
        description: formData.description,
        language: formData.language,
        model: formData.model,
        behavior: formData.behaviorPrompt,
        respond_to: formData.respondTo,
        whitelist: formData.respondTo === "whitelist" ? formData.whitelist : [],
        features: formData.features,
        handoff_config: formData.handoffConfig,
        booking_config: formData.bookingConfig,
        email_config: formData.emailConfig,
      });

      // Update local state in AppContext
      updateAgent(id, formData);

      // Update local agent state
      setAgent((prev) => ({ ...prev, ...formData }));

      setHasChanges(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save settings:", err);
      setSaveError(err.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Section-specific save handler
  const handleSaveSection = async (sectionName, sectionData) => {
    setSectionSaving((prev) => ({ ...prev, [sectionName]: true }));
    setSaveError(null);

    try {
      await api.updateAgentSettings(id, sectionData);

      // Update local state
      updateAgent(id, formData);
      setAgent((prev) => ({ ...prev, ...formData }));

      setHasChanges(false);
      setSectionSaved((prev) => ({ ...prev, [sectionName]: true }));
      setTimeout(
        () => setSectionSaved((prev) => ({ ...prev, [sectionName]: false })),
        2000,
      );
    } catch (err) {
      console.error(`Failed to save ${sectionName}:`, err);
      setSaveError(err.message || `Failed to save ${sectionName}`);
    } finally {
      setSectionSaving((prev) => ({ ...prev, [sectionName]: false }));
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // First disconnect WhatsApp session if connected
      const isWhatsAppConnected =
        agent.whatsappConnected ||
        agent.whatsapp_status === "connected" ||
        !!agent.whatsapp_session_id;

      if (isWhatsAppConnected) {
        try {
          await api.disconnectWhatsApp(id);
        } catch (err) {
          // Continue with deletion even if WhatsApp disconnect fails
          console.error("Failed to disconnect WhatsApp:", err);
        }
      }

      // Delete the agent
      await api.deleteAgent(id);

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete agent:", err);
      setSaveError(err.message || "Failed to delete agent");
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    setIsDisconnecting(true);
    try {
      await api.disconnectWhatsApp(id);
      // Update local agent state to reflect disconnection
      setAgent((prev) => ({
        ...prev,
        whatsappConnected: false,
        whatsapp_status: null,
        whatsapp_session_id: null,
        whatsapp_phone_number: null,
      }));
      setShowDisconnectConfirm(false);
    } catch (err) {
      console.error("Failed to disconnect WhatsApp:", err);
    } finally {
      setIsDisconnecting(false);
    }
  };

  // Handle pause/resume bot
  const handleToggleBotStatus = async () => {
    setIsTogglingStatus(true);
    setSaveError(null);

    try {
      const isCurrentlyActive =
        agent.status === "active" || agent.status === "live";

      if (isCurrentlyActive) {
        // Pause the bot
        await api.deactivateAgent(id);
        setAgent((prev) => ({ ...prev, status: "paused" }));
      } else {
        // Resume the bot
        await api.launchAgent(id, {
          respond_to: formData.respondTo,
          whitelist:
            formData.respondTo === "whitelist" ? formData.whitelist : [],
        });
        setAgent((prev) => ({ ...prev, status: "active" }));
      }
    } catch (err) {
      console.error("Failed to toggle bot status:", err);
      setSaveError(err.message || "Failed to change bot status");
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // WhatsApp connection modal handlers
  const openConnectModal = () => {
    setShowConnectModal(true);
    setConnectStep("phone");
    setConnectPhoneNumber("");
    setConnectError(null);
    setQrImageUrl(null);
    setQrCountdown(60);
  };

  const closeConnectModal = useCallback(() => {
    // Clear intervals
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setShowConnectModal(false);
    setConnectStep("phone");
    setConnectPhoneNumber("");
    setConnectError(null);
    setQrImageUrl(null);
  }, []);

  // Validate phone number format
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\+[1-9]\d{6,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ""));
  };

  // Start QR countdown timer
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setQrCountdown(60);
    countdownIntervalRef.current = setInterval(() => {
      setQrCountdown((prev) => {
        if (prev <= 1) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Start polling for connection status
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await api.getWhatsAppStatus(id);
        if (result.status === "connected") {
          // Clear intervals
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Update state
          setConnectStep("connected");
          setAgent((prev) => ({
            ...prev,
            whatsappConnected: true,
            whatsapp_status: "connected",
            whatsapp_session_id: result.session_id,
            whatsapp_phone_number: result.phone_number || connectPhoneNumber,
          }));
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);
  }, [id, connectPhoneNumber]);

  // Handle generate QR code
  const handleGenerateQR = async () => {
    const cleanPhone = connectPhoneNumber.replace(/\s/g, "");

    if (!cleanPhone) {
      setConnectError("Please enter your WhatsApp phone number.");
      return;
    }

    if (!isValidPhoneNumber(cleanPhone)) {
      setConnectError(
        "Please enter a valid phone number in international format (e.g., +254712345678)",
      );
      return;
    }

    setConnectError(null);
    setIsConnecting(true);

    try {
      const result = await api.pairWhatsApp(id, cleanPhone);

      if (result.status === "connected") {
        setConnectStep("connected");
        setAgent((prev) => ({
          ...prev,
          whatsappConnected: true,
          whatsapp_status: "connected",
          whatsapp_session_id: result.session_id,
          whatsapp_phone_number: cleanPhone,
        }));
        return;
      }

      // Move to QR step
      if (result.qr_image_url) {
        setQrImageUrl(result.qr_image_url);
      }
      setConnectStep("qr");
      startCountdown();
      startPolling();
    } catch (err) {
      setConnectError(err.message || "Failed to start WhatsApp pairing");
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle refresh QR code
  const handleRefreshQR = async () => {
    setConnectError(null);
    setIsConnecting(true);

    try {
      const result = await api.refreshWhatsAppQR(id);
      if (result.qr_image_url) {
        setQrImageUrl(result.qr_image_url);
      }
      startCountdown();
    } catch (err) {
      setConnectError(err.message || "Failed to refresh QR code");
    } finally {
      setIsConnecting(false);
    }
  };

  // Handle reconnect - uses existing session, skips phone number step
  const handleReconnect = async () => {
    setConnectError(null);
    setIsConnecting(true);

    try {
      // Use existing session - skip phone number step
      const result = await api.refreshWhatsAppQR(id);

      if (result.status === "connected") {
        // Already connected (maybe from another device)
        setAgent((prev) => ({
          ...prev,
          whatsapp_status: "connected",
        }));
        return;
      }

      // Show QR code directly in modal
      if (result.qr_image_url) {
        setQrImageUrl(result.qr_image_url);
      }
      setShowConnectModal(true);
      setConnectStep("qr"); // Skip phone step, go directly to QR
      startCountdown();
      startPolling();
    } catch (err) {
      setConnectError(err.message || "Failed to reconnect WhatsApp");
    } finally {
      setIsConnecting(false);
    }
  };

  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Agent is loaded by the parent layout, so we just need to check if it exists
  if (!agent) return null;

  // Check WhatsApp connection status - whatsapp_status is the source of truth
  const isWhatsAppConnected = agent.whatsapp_status === "connected";
  const needsReconnect = ["disconnected", "need_scan", "logged_out", "expired"].includes(agent.whatsapp_status);
  const phoneNumber = agent.phoneNumber || agent.whatsapp_phone_number;

  // Note: autoReply is always enabled by default (core functionality)
  const features = [
    {
      key: "leadCapture",
      label: "Lead Capture",
      description: "Collect contact information from conversations",
    },
    {
      key: "appointmentBooking",
      label: "Appointment Booking",
      description: "Allow scheduling appointments through chat",
    },
    {
      key: "gmailIntegration",
      label: "Gmail Integration",
      description: "Send emails to customers via your Gmail",
    },
    {
      key: "humanHandoff",
      label: "Human Handoff",
      description: "Transfer complex queries to humans",
    },
  ];

  return (
    <div className="p-8 animate-fade-in max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <SettingsIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
            <p className="text-text-secondary">Configure your AI assistant</p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className={`
            btn-primary flex items-center gap-2 transition-all
            ${!hasChanges || isSaving ? "opacity-50 cursor-not-allowed" : ""}
            ${showSaved ? "bg-success" : ""}
          `}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : showSaved ? (
            <>
              <Check className="w-4 h-4" />
              Saved!
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Save Error */}
      {saveError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{saveError}</span>
          <button
            onClick={() => setSaveError(null)}
            className="ml-auto p-1 hover:bg-red-100 rounded transition-colors"
          >
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {/* Bot Status */}
      <section className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Power className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">
            Bot Status
          </h2>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {agent.status === "active" || agent.status === "live" ? (
                <>
                  <span className="w-2.5 h-2.5 rounded-full bg-success animate-pulse" />
                  <span className="font-medium text-success">Live</span>
                </>
              ) : (
                <>
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="font-medium text-amber-600">Paused</span>
                </>
              )}
            </div>
            <p className="text-sm text-text-muted">
              {agent.status === "active" || agent.status === "live"
                ? "Your bot is actively responding to messages"
                : "Your bot is paused and will not respond to messages"}
            </p>
          </div>
          <button
            onClick={handleToggleBotStatus}
            disabled={isTogglingStatus}
            className={`
              px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors disabled:opacity-50
              ${
                agent.status === "active" || agent.status === "live"
                  ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                  : "bg-success/10 text-success hover:bg-success/20"
              }
            `}
          >
            {isTogglingStatus ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {agent.status === "active" || agent.status === "live"
                  ? "Pausing..."
                  : "Resuming..."}
              </>
            ) : agent.status === "active" || agent.status === "live" ? (
              <>
                <PauseCircle className="w-4 h-4" />
                Pause Bot
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" />
                Resume Bot
              </>
            )}
          </button>
        </div>
      </section>

      {/* Basic Info */}
      <section className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-text-primary">
              Basic Information
            </h2>
          </div>
          <button
            onClick={() =>
              handleSaveSection("basicInfo", {
                name: formData.name,
                business_type: formData.businessType,
                description: formData.description,
                language: formData.language,
              })
            }
            disabled={sectionSaving.basicInfo}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all
              ${
                sectionSaved.basicInfo
                  ? "bg-success text-white"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              }
              ${sectionSaving.basicInfo ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            {sectionSaving.basicInfo ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving...
              </>
            ) : sectionSaved.basicInfo ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              Bot Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="input-field"
              placeholder="e.g., Sales Assistant"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                Business Type
              </label>
              <input
                type="text"
                value={formData.businessType}
                onChange={(e) => handleChange("businessType", e.target.value)}
                className="input-field"
                placeholder="e.g., Restaurant, E-commerce Store, Salon"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1.5">
                <Globe className="w-4 h-4 inline mr-1" />
                Language
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                  className="w-full input-field flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg bg-primary/10">
                      <Globe className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium text-text-primary">
                      {formData.language || "Select Language"}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-text-muted transition-transform ${showLanguageSelector ? "rotate-180" : ""}`}
                  />
                </button>

                {/* Language Dropdown */}
                {showLanguageSelector && (
                  <div className="absolute z-20 mt-2 w-full bg-white border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto animate-fade-in">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => {
                          handleChange("language", lang);
                          setShowLanguageSelector(false);
                        }}
                        className={`w-full p-3 flex items-center gap-3 hover:bg-background transition-colors text-left ${
                          formData.language === lang ? "bg-primary/5" : ""
                        }`}
                      >
                        <div className="p-1.5 rounded-lg bg-primary/10 flex-shrink-0">
                          <Globe className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-medium text-text-primary flex-1">
                          {lang}
                        </span>
                        {formData.language === lang && (
                          <Check className="w-4 h-4 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-text-primary">
                Business Description
              </label>
              <button
                type="button"
                onClick={handleFormatDescription}
                disabled={
                  isFormattingDescription || !formData.description.trim()
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFormattingDescription ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                {isFormattingDescription ? "Formatting..." : "Format with AI"}
              </button>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              disabled={isFormattingDescription}
              className="input-field resize-none disabled:opacity-50 disabled:cursor-not-allowed"
              rows={3}
              placeholder="Describe what your business does..."
            />
          </div>
        </div>
      </section>

      {/* Behavior */}
      <section className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-text-primary">
              AI Behavior
            </h2>
          </div>
          <button
            onClick={() =>
              handleSaveSection("behavior", {
                model: formData.model,
                behavior: formData.behaviorPrompt,
              })
            }
            disabled={sectionSaving.behavior}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all
              ${
                sectionSaved.behavior
                  ? "bg-success text-white"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              }
              ${sectionSaving.behavior ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            {sectionSaving.behavior ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving...
              </>
            ) : sectionSaved.behavior ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              <Cpu className="w-4 h-4 inline mr-1" />
              AI Model
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="w-full input-field flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-lg bg-primary/10">
                    <Cpu className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium text-text-primary">
                      {llmModels.find((m) => m.id === formData.model)?.name ||
                        "Select Model"}
                    </span>
                    <span className="text-text-muted ml-2 text-sm">
                      (
                      {llmModels.find((m) => m.id === formData.model)
                        ?.provider || "Provider"}
                      )
                    </span>
                  </div>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-text-muted transition-transform ${showModelSelector ? "rotate-180" : ""}`}
                />
              </button>

              {/* Model Dropdown */}
              {showModelSelector && (
                <div className="absolute z-20 mt-2 w-full bg-white border border-border rounded-xl shadow-lg max-h-80 overflow-y-auto animate-fade-in">
                  {llmModels.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => {
                        handleChange("model", model.id);
                        setShowModelSelector(false);
                      }}
                      className={`w-full p-3 flex items-start gap-3 hover:bg-background transition-colors text-left ${
                        formData.model === model.id ? "bg-primary/5" : ""
                      }`}
                    >
                      <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                        <Cpu className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-text-primary">
                            {model.name}
                          </span>
                          {model.badge && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full ${model.badgeColor}`}
                            >
                              {model.badge}
                            </span>
                          )}
                          {formData.model === model.id && (
                            <Check className="w-4 h-4 text-primary ml-auto" />
                          )}
                        </div>
                        <p className="text-xs text-text-muted mt-0.5">
                          {model.provider}
                        </p>
                        <p className="text-xs text-text-secondary mt-1 line-clamp-2">
                          {model.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-text-muted mt-1">
              The AI model powering your assistant's responses.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-text-primary">
                Behavior Prompt
              </label>
              <button
                type="button"
                onClick={handleFormatBehavior}
                disabled={
                  isFormattingBehavior || !formData.behaviorPrompt.trim()
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFormattingBehavior ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                {isFormattingBehavior ? "Formatting..." : "Format with AI"}
              </button>
            </div>
            <textarea
              value={formData.behaviorPrompt}
              onChange={(e) => handleChange("behaviorPrompt", e.target.value)}
              disabled={isFormattingBehavior}
              className="input-field resize-none font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              rows={6}
              placeholder="You are a helpful assistant for..."
            />
            <p className="text-xs text-text-muted mt-2">
              This prompt defines how your AI assistant will behave and respond
              to customers.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ToggleLeft className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-text-primary">
              Features
            </h2>
          </div>
          <button
            onClick={() =>
              handleSaveSection("features", {
                features: formData.features,
                handoff_config: formData.handoffConfig,
                booking_config: formData.bookingConfig,
              })
            }
            disabled={sectionSaving.features}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all
              ${
                sectionSaved.features
                  ? "bg-success text-white"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              }
              ${sectionSaving.features ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            {sectionSaving.features ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving...
              </>
            ) : sectionSaved.features ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          {features.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between py-2">
              <div>
                <h3 className="font-medium text-text-primary">{label}</h3>
                <p className="text-sm text-text-muted">{description}</p>
              </div>
              <Toggle
                checked={formData.features[key]}
                onChange={() => handleFeatureToggle(key)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Response Mode */}
      <section className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-text-primary">
              Response Mode
            </h2>
          </div>
          <button
            onClick={() =>
              handleSaveSection("responseMode", {
                respond_to: formData.respondTo,
                whitelist:
                  formData.respondTo === "whitelist" ? formData.whitelist : [],
              })
            }
            disabled={sectionSaving.responseMode}
            className={`
              px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all
              ${
                sectionSaved.responseMode
                  ? "bg-success text-white"
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              }
              ${sectionSaving.responseMode ? "opacity-50 cursor-not-allowed" : ""}
            `}
          >
            {sectionSaving.responseMode ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Saving...
              </>
            ) : sectionSaved.responseMode ? (
              <>
                <Check className="w-3.5 h-3.5" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        </div>

        <p className="text-sm text-text-muted mb-4">
          Who should your AI respond to?
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <button
            type="button"
            onClick={() => handleChange("respondTo", "all")}
            className={`
              p-4 rounded-xl border-2 text-left transition-all
              ${
                formData.respondTo === "all"
                  ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                  : "border-border hover:border-primary/30"
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`
                p-2 rounded-lg
                ${formData.respondTo === "all" ? "bg-primary text-white" : "bg-primary/10 text-primary"}
              `}
              >
                <Users className="w-5 h-5" />
              </div>
              <span className="font-semibold text-text-primary">
                All Contacts
              </span>
            </div>
            <p className="text-sm text-text-muted">
              Respond to messages from anyone who contacts your WhatsApp
            </p>
          </button>

          <button
            type="button"
            onClick={() => handleChange("respondTo", "whitelist")}
            className={`
              p-4 rounded-xl border-2 text-left transition-all
              ${
                formData.respondTo === "whitelist"
                  ? "border-primary ring-2 ring-primary/20 bg-primary/5"
                  : "border-border hover:border-primary/30"
              }
            `}
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`
                p-2 rounded-lg
                ${formData.respondTo === "whitelist" ? "bg-primary text-white" : "bg-primary/10 text-primary"}
              `}
              >
                <UserCheck className="w-5 h-5" />
              </div>
              <span className="font-semibold text-text-primary">
                Specific Numbers
              </span>
            </div>
            <p className="text-sm text-text-muted">
              Only respond to specific phone numbers you approve
            </p>
          </button>
        </div>

        {/* Whitelist Input */}
        {formData.respondTo === "whitelist" && (
          <div className="mt-4 p-4 bg-background rounded-xl border border-border animate-fade-in">
            <div className="flex gap-2">
              <input
                type="tel"
                placeholder="254712345678"
                value={whitelistInput}
                onChange={(e) => setWhitelistInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleAddWhitelistNumber()
                }
                className="input-field flex-1"
              />
              <button
                type="button"
                onClick={handleAddWhitelistNumber}
                disabled={!whitelistInput.trim()}
                className="btn-secondary px-4 disabled:opacity-50"
              >
                Add
              </button>
            </div>

            {formData.whitelist.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-text-muted mb-2">
                  Approved numbers:
                </p>
                <div className="flex flex-wrap gap-2">
                  {formData.whitelist.map((number, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-border rounded-lg text-sm"
                    >
                      {number}
                      <button
                        type="button"
                        onClick={() => handleRemoveWhitelistNumber(number)}
                        className="text-text-muted hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* WhatsApp Connection */}
      <section id="whatsapp-section" className="card p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">
            WhatsApp Connection
          </h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-primary">
                {isWhatsAppConnected ? (
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                    Connected to {phoneNumber || "WhatsApp"}
                  </span>
                ) : needsReconnect ? (
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    {agent.whatsapp_status === "need_scan"
                      ? "QR code scan needed"
                      : "Session disconnected"}
                  </span>
                ) : (
                  <span className="text-text-muted">
                    No WhatsApp account connected
                  </span>
                )}
              </p>
            </div>
            {!isWhatsAppConnected && (
              <button
                onClick={needsReconnect ? handleReconnect : openConnectModal}
                disabled={isConnecting && needsReconnect}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting && needsReconnect ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {isConnecting && needsReconnect ? "Reconnecting..." : needsReconnect ? "Reconnect" : "Connect"}
              </button>
            )}
          </div>

          {/* Disconnect WhatsApp - Show if session exists (even if disconnected) */}
          {agent.whatsapp_session_id && (
            <div className="pt-4 border-t border-border">
              {!showDisconnectConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-text-primary">
                      Disconnect WhatsApp
                    </h3>
                    <p className="text-sm text-text-muted">
                      Remove WhatsApp connection and delete session.
                    </p>
                    <p className="text-sm text-text-muted">
                      You'll need to scan the QR code again to reconnect.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg font-medium hover:bg-amber-200 transition-colors flex items-center gap-2"
                  >
                    <Unplug className="w-4 h-4" />
                    Disconnect
                  </button>
                </div>
              ) : (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-text-primary mb-4">
                    Are you sure you want to disconnect WhatsApp from{" "}
                    <strong>{agent.name}</strong>? This will delete the session
                    and you'll need to scan the QR code again to reconnect.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleDisconnectWhatsApp}
                      disabled={isDisconnecting}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isDisconnecting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Disconnecting...
                        </>
                      ) : (
                        <>
                          <Unplug className="w-4 h-4" />
                          Yes, Disconnect
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowDisconnectConfirm(false)}
                      disabled={isDisconnecting}
                      className="px-4 py-2 bg-gray-100 text-text-secondary rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Danger Zone */}
      <section className="card p-6 border-red-200 bg-red-50/50">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h2 className="text-lg font-semibold text-red-700">Danger Zone</h2>
        </div>

        {!showDeleteConfirm ? (
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-text-primary">Delete Agent</h3>
              <p className="text-sm text-text-muted">
                Permanently delete this agent and all its data
              </p>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-100 text-red-600 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Agent
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-4 border border-red-200">
            <p className="text-text-primary mb-4">
              Are you sure you want to delete <strong>{agent.name}</strong>?
              This will also disconnect the WhatsApp session. This action cannot
              be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Yes, Delete Agent
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 bg-gray-100 text-text-secondary rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* WhatsApp Connect Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-green-100">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-text-primary">
                  Connect WhatsApp
                </h2>
              </div>
              <button
                onClick={closeConnectModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1 scrollbar-thin">
              {/* Step 1: Phone Number Input */}
              {connectStep === "phone" && (
                <div className="animate-fade-in">
                  {/* WhatsApp Icon */}
                  <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-2xl bg-green-50">
                      <svg
                        className="w-12 h-12 text-green-500"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-1">
                      Enter your phone number
                    </h3>
                    <p className="text-sm text-text-secondary">
                      We'll generate a QR code for you to scan
                    </p>
                  </div>

                  {/* Phone Input */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      WhatsApp Phone Number
                    </label>
                    <input
                      type="tel"
                      value={connectPhoneNumber}
                      onChange={(e) => setConnectPhoneNumber(e.target.value)}
                      placeholder="+254712345678"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                    <p className="mt-1.5 text-xs text-text-muted">
                      Enter in international format with country code (e.g.,
                      +254 for Kenya, +255 for Tanzania, +256 for Uganda)
                    </p>
                    <p className="mt-1 text-xs text-amber-600 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 rounded-full bg-amber-500"></span>
                      This number must already be active on WhatsApp
                    </p>
                  </div>

                  {/* Error Display */}
                  {connectError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{connectError}</p>
                    </div>
                  )}

                  {/* Generate QR Button */}
                  <button
                    onClick={handleGenerateQR}
                    disabled={isConnecting || !connectPhoneNumber.trim()}
                    className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating QR Code...
                      </>
                    ) : (
                      <>
                        <QrCode className="w-4 h-4" />
                        Generate QR Code
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Step 2: QR Code Display */}
              {connectStep === "qr" && (
                <div className="animate-fade-in">
                  {/* Error Display */}
                  {connectError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{connectError}</p>
                    </div>
                  )}

                  {/* QR Code */}
                  <div className="flex justify-center mb-6">
                    <div
                      className={`relative p-4 bg-white rounded-2xl shadow-soft border border-gray-100 ${isConnecting ? "animate-pulse" : ""}`}
                    >
                      {qrImageUrl ? (
                        <img
                          src={qrImageUrl}
                          alt="WhatsApp QR Code"
                          className="w-48 h-48 object-contain"
                          onError={() =>
                            setConnectError("Failed to load QR code image")
                          }
                        />
                      ) : (
                        <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded-lg">
                          {isConnecting ? (
                            <div className="flex flex-col items-center">
                              <Loader2 className="w-8 h-8 text-primary animate-spin" />
                              <p className="mt-2 text-sm text-text-muted">
                                Loading...
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-text-muted text-center px-4">
                              QR code not available.
                              <br />
                              Click refresh below.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Loading Overlay */}
                      {isConnecting && qrImageUrl && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-2xl">
                          <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Phone Number Reminder for Reconnection */}
                  {needsReconnect && phoneNumber && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
                      <Phone className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Scan with: {phoneNumber}
                        </p>
                        <p className="text-xs text-blue-600">
                          Use this WhatsApp number to scan the QR code
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div className="text-center mb-6">
                    {qrCountdown > 0 ? (
                      <>
                        <p className="text-text-secondary mb-1">
                          Scan this code with WhatsApp on your phone
                        </p>
                        <p className="text-sm text-text-muted">
                          Code expires in{" "}
                          <span className="font-mono font-medium text-primary">
                            {formatTime(qrCountdown)}
                          </span>
                        </p>
                      </>
                    ) : (
                      <p className="text-amber-600 font-medium">
                        QR code expired. Please generate a new one.
                      </p>
                    )}
                  </div>

                  {/* Refresh Button */}
                  <button
                    onClick={handleRefreshQR}
                    disabled={isConnecting}
                    className="w-full btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${isConnecting ? "animate-spin" : ""}`}
                    />
                    {qrCountdown === 0
                      ? "Generate New Code"
                      : "Refresh QR Code"}
                  </button>

                  {/* Instructions - Collapsible */}
                  <div className="mt-6 bg-gray-50 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setShowScanInstructions(!showScanInstructions)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                    >
                      <h4 className="text-sm font-medium text-text-primary">
                        How to scan
                      </h4>
                      <ChevronDown
                        className={`w-4 h-4 text-text-muted transition-transform duration-200 ${
                          showScanInstructions ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {showScanInstructions && (
                      <ol className="px-4 pb-4 text-xs text-text-secondary space-y-1 animate-fade-in">
                        <li>1. Open WhatsApp on your phone</li>
                        <li>2. Tap Menu or Settings</li>
                        <li>3. Tap Linked Devices</li>
                        <li>4. Tap "Link a Device"</li>
                        <li>5. Point your phone at this screen</li>
                      </ol>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Connected Success */}
              {connectStep === "connected" && (
                <div className="animate-fade-in text-center">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full bg-success/10">
                      <CheckCircle className="w-16 h-16 text-success" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-text-primary mb-2">
                    Connected!
                  </h3>
                  <p className="text-text-secondary mb-6">
                    Your WhatsApp is now linked to {agent.name}
                  </p>

                  <button
                    onClick={closeConnectModal}
                    className="w-full btn-primary"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Human Handoff Config Modal */}
      {showHandoffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col animate-fade-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <MessageCircle className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-semibold text-text-primary">
                  Human Handoff Settings
                </h2>
              </div>
              <button
                onClick={handleCloseHandoffModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1 scrollbar-thin">
              <p className="text-text-secondary mb-6">
                When a customer asks to speak to a human or if the AI is unable
                to assist, your AI will provide these contact details. At least
                one contact method is required.
              </p>

              {/* Error Display */}
              {handoffError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{handoffError}</p>
                </div>
              )}

              {/* Phone Number */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Phone className="w-4 h-4 inline mr-1.5" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={handoffConfigTemp.phone}
                  onChange={(e) =>
                    setHandoffConfigTemp((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  placeholder="+254712345678"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Phone number customers can call or WhatsApp
                </p>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <Mail className="w-4 h-4 inline mr-1.5" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={handoffConfigTemp.email}
                  onChange={(e) =>
                    setHandoffConfigTemp((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  placeholder="support@yourbusiness.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Email address for customer inquiries
                </p>
              </div>

              {/* Custom Message */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  <MessageCircle className="w-4 h-4 inline mr-1.5" />
                  Custom Message (Optional)
                </label>
                <textarea
                  value={handoffConfigTemp.message}
                  onChange={(e) =>
                    setHandoffConfigTemp((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                  placeholder="e.g., Our team is available Monday-Friday, 9am-5pm EAT."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                  rows={3}
                />
                <p className="mt-1 text-xs text-text-muted">
                  Additional message to show when handing off to a human
                </p>
              </div>

              {/* Preview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-xs font-medium text-text-muted uppercase tracking-wide mb-2">
                  Preview
                </p>
                <p className="text-sm text-text-primary">
                  {handoffConfigTemp.message ||
                    "I understand you'd like to speak with a human."}{" "}
                  You can reach us at:
                </p>
                <ul className="mt-2 text-sm text-text-secondary space-y-1">
                  {handoffConfigTemp.phone && (
                    <li className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-primary" />
                      {handoffConfigTemp.phone}
                    </li>
                  )}
                  {handoffConfigTemp.email && (
                    <li className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-primary" />
                      {handoffConfigTemp.email}
                    </li>
                  )}
                  {!handoffConfigTemp.phone && !handoffConfigTemp.email && (
                    <li className="text-text-muted italic">
                      Add a phone or email to see preview
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button
                onClick={handleSaveHandoffConfig}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Enable Human Handoff
              </button>
              <button
                onClick={handleCloseHandoffModal}
                className="px-4 py-2 bg-gray-100 text-text-secondary rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Booking Config Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-scale-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    Appointment Booking Setup
                  </h2>
                  <p className="text-sm text-text-secondary">
                    Configure your availability
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseBookingModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
              {bookingError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {bookingError}
                </div>
              )}

              {/* Model Recommendation Notice */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Recommended: Use GPT Models</p>
                  <p className="text-sm text-blue-700 mt-1">
                    For appointment booking to work properly, we recommend using GPT models such as <strong>GPT-4o</strong> or <strong>GPT-4o-mini</strong>. You can change this in the AI Model section above.
                  </p>
                </div>
              </div>

              {/* Basic Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-text-primary">
                  Basic Settings
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {/* Timezone Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Timezone
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenBookingDropdown(
                            openBookingDropdown === "timezone"
                              ? null
                              : "timezone",
                          )
                        }
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary truncate">
                          {timezoneOptions.find(
                            (o) => o.value === bookingConfigTemp.timezone,
                          )?.label || "Select timezone"}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === "timezone" ? "rotate-180" : ""}`}
                        />
                      </button>
                      {openBookingDropdown === "timezone" && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {timezoneOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({
                                  ...prev,
                                  timezone: option.value,
                                }));
                                setOpenBookingDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span
                                className={
                                  bookingConfigTemp.timezone === option.value
                                    ? "text-primary font-medium"
                                    : "text-text-primary"
                                }
                              >
                                {option.label}
                              </span>
                              {bookingConfigTemp.timezone === option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Slot Duration Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Slot Duration
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenBookingDropdown(
                            openBookingDropdown === "duration"
                              ? null
                              : "duration",
                          )
                        }
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {slotDurationOptions.find(
                            (o) =>
                              o.value ===
                              bookingConfigTemp.slot_duration_minutes,
                          )?.label || "Select duration"}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === "duration" ? "rotate-180" : ""}`}
                        />
                      </button>
                      {openBookingDropdown === "duration" && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {slotDurationOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({
                                  ...prev,
                                  slot_duration_minutes: option.value,
                                }));
                                setOpenBookingDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span
                                className={
                                  bookingConfigTemp.slot_duration_minutes ===
                                  option.value
                                    ? "text-primary font-medium"
                                    : "text-text-primary"
                                }
                              >
                                {option.label}
                              </span>
                              {bookingConfigTemp.slot_duration_minutes ===
                                option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Minimum Advance Booking Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Minimum Advance Booking
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenBookingDropdown(
                            openBookingDropdown === "minAdvance"
                              ? null
                              : "minAdvance",
                          )
                        }
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {minAdvanceOptions.find(
                            (o) =>
                              o.value === bookingConfigTemp.min_advance_hours,
                          )?.label || "Select minimum"}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === "minAdvance" ? "rotate-180" : ""}`}
                        />
                      </button>
                      {openBookingDropdown === "minAdvance" && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {minAdvanceOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({
                                  ...prev,
                                  min_advance_hours: option.value,
                                }));
                                setOpenBookingDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span
                                className={
                                  bookingConfigTemp.min_advance_hours ===
                                  option.value
                                    ? "text-primary font-medium"
                                    : "text-text-primary"
                                }
                              >
                                {option.label}
                              </span>
                              {bookingConfigTemp.min_advance_hours ===
                                option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Max Advance Days Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Max Advance Days
                    </label>
                    <div className="relative booking-dropdown">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenBookingDropdown(
                            openBookingDropdown === "maxAdvance"
                              ? null
                              : "maxAdvance",
                          )
                        }
                        className="w-full px-3 py-2 bg-white border border-border rounded-xl text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                      >
                        <span className="text-sm text-text-primary">
                          {maxAdvanceOptions.find(
                            (o) =>
                              o.value === bookingConfigTemp.max_advance_days,
                          )?.label || "Select maximum"}
                        </span>
                        <ChevronDown
                          className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === "maxAdvance" ? "rotate-180" : ""}`}
                        />
                      </button>
                      {openBookingDropdown === "maxAdvance" && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                          {maxAdvanceOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                setBookingConfigTemp((prev) => ({
                                  ...prev,
                                  max_advance_days: option.value,
                                }));
                                setOpenBookingDropdown(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                            >
                              <span
                                className={
                                  bookingConfigTemp.max_advance_days ===
                                  option.value
                                    ? "text-primary font-medium"
                                    : "text-text-primary"
                                }
                              >
                                {option.label}
                              </span>
                              {bookingConfigTemp.max_advance_days ===
                                option.value && (
                                <Check className="w-4 h-4 text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Clock className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-text-primary">
                      Working Hours
                    </h3>
                    <p className="text-sm text-text-secondary">
                      Set your available days and hours for appointments
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {[
                    { num: "1", name: "Monday" },
                    { num: "2", name: "Tuesday" },
                    { num: "3", name: "Wednesday" },
                    { num: "4", name: "Thursday" },
                    { num: "5", name: "Friday" },
                    { num: "6", name: "Saturday" },
                    { num: "0", name: "Sunday" },
                  ].map((day) => {
                    const isEnabled =
                      bookingConfigTemp.working_hours[day.num] !== null;
                    return (
                      <div
                        key={day.num}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                          isEnabled
                            ? "border-primary/20 bg-primary/5"
                            : "border-border bg-gray-50"
                        }`}
                      >
                        <label className="flex items-center gap-3 min-w-[120px] cursor-pointer">
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                              isEnabled
                                ? "bg-primary border-primary"
                                : "border-gray-300 bg-white"
                            }`}
                          >
                            {isEnabled && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => toggleWorkingDay(day.num)}
                            className="sr-only"
                          />
                          <span
                            className={`text-sm font-medium ${isEnabled ? "text-text-primary" : "text-text-muted"}`}
                          >
                            {day.name}
                          </span>
                        </label>

                        {isEnabled ? (
                          <div className="flex items-center gap-2 flex-1">
                            {/* Start Time Dropdown */}
                            <div className="relative booking-dropdown flex-1">
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenBookingDropdown(
                                    openBookingDropdown === `start-${day.num}`
                                      ? null
                                      : `start-${day.num}`,
                                  )
                                }
                                className="w-full px-3 py-2 bg-white border border-border rounded-lg text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                              >
                                <span className="text-sm text-text-primary">
                                  {formatTimeLabel(
                                    bookingConfigTemp.working_hours[day.num]
                                      ?.start,
                                  )}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === `start-${day.num}` ? "rotate-180" : ""}`}
                                />
                              </button>
                              {openBookingDropdown === `start-${day.num}` && (
                                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {timeOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        updateWorkingHours(
                                          day.num,
                                          "start",
                                          option.value,
                                        );
                                        setOpenBookingDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                                    >
                                      <span
                                        className={
                                          bookingConfigTemp.working_hours[
                                            day.num
                                          ]?.start === option.value
                                            ? "text-primary font-medium"
                                            : "text-text-primary"
                                        }
                                      >
                                        {option.label}
                                      </span>
                                      {bookingConfigTemp.working_hours[day.num]
                                        ?.start === option.value && (
                                        <Check className="w-4 h-4 text-primary" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <span className="text-text-muted text-sm">to</span>

                            {/* End Time Dropdown */}
                            <div className="relative booking-dropdown flex-1">
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenBookingDropdown(
                                    openBookingDropdown === `end-${day.num}`
                                      ? null
                                      : `end-${day.num}`,
                                  )
                                }
                                className="w-full px-3 py-2 bg-white border border-border rounded-lg text-left flex items-center justify-between hover:border-primary/50 transition-colors"
                              >
                                <span className="text-sm text-text-primary">
                                  {formatTimeLabel(
                                    bookingConfigTemp.working_hours[day.num]
                                      ?.end,
                                  )}
                                </span>
                                <ChevronDown
                                  className={`w-4 h-4 text-text-muted transition-transform ${openBookingDropdown === `end-${day.num}` ? "rotate-180" : ""}`}
                                />
                              </button>
                              {openBookingDropdown === `end-${day.num}` && (
                                <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                  {timeOptions.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      onClick={() => {
                                        updateWorkingHours(
                                          day.num,
                                          "end",
                                          option.value,
                                        );
                                        setOpenBookingDropdown(null);
                                      }}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-primary/5 flex items-center justify-between transition-colors first:rounded-t-xl last:rounded-b-xl"
                                    >
                                      <span
                                        className={
                                          bookingConfigTemp.working_hours[
                                            day.num
                                          ]?.end === option.value
                                            ? "text-primary font-medium"
                                            : "text-text-primary"
                                        }
                                      >
                                        {option.label}
                                      </span>
                                      {bookingConfigTemp.working_hours[day.num]
                                        ?.end === option.value && (
                                        <Check className="w-4 h-4 text-primary" />
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-text-muted italic">
                            Closed
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Google Calendar Integration */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-blue-50">
                    <svg
                      className="w-4 h-4 text-blue-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19.5 3h-15A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h15a1.5 1.5 0 001.5-1.5v-15A1.5 1.5 0 0019.5 3zm-9 15h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3zm4.5 9h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3zm4.5 9h-3v-3h3v3zm0-4.5h-3v-3h3v3zm0-4.5h-3V6h3v3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-text-primary">
                      Google Calendar
                    </h3>
                    <p className="text-sm text-text-secondary">
                      Sync appointments with your calendar
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white border border-border flex items-center justify-center shadow-sm">
                        <svg className="w-6 h-6" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">
                          Connect Google Calendar
                        </p>
                        <p className="text-xs text-text-muted">
                          Auto-sync bookings to your calendar
                        </p>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                      Coming Soon
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Confirmation Message (Optional)
                </label>
                <textarea
                  placeholder="Your appointment is confirmed for {date} at {time}. We look forward to seeing you!"
                  value={bookingConfigTemp.confirmation_message}
                  onChange={(e) =>
                    setBookingConfigTemp((prev) => ({
                      ...prev,
                      confirmation_message: e.target.value,
                    }))
                  }
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                />
                <p className="text-xs text-text-muted">
                  Use {"{date}"}, {"{time}"}, {"{customer_name}"} as
                  placeholders
                </p>
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button
                onClick={handleSaveBookingConfig}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Enable Appointment Booking
              </button>
              <button
                onClick={handleCloseBookingModal}
                className="px-4 py-2 bg-gray-100 text-text-secondary rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Integration Config Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-scale-in">
            {/* Modal Header - Fixed */}
            <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    Gmail Integration Setup
                  </h2>
                  <p className="text-sm text-text-secondary">
                    Connect your Gmail to send emails
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseEmailModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
              {emailError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {emailError}
                </div>
              )}

              {testEmailResult && (
                <div
                  className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                    testEmailResult.success
                      ? "bg-green-50 border border-green-200 text-green-700"
                      : "bg-red-50 border border-red-200 text-red-700"
                  }`}
                >
                  {testEmailResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  {testEmailResult.message}
                </div>
              )}

              {/* Instructions Section */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 mb-2">
                      How to get your App Password
                    </h3>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li>
                        Go to your Google Account settings at{" "}
                        <a
                          href="https://myaccount.google.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-medium"
                        >
                          myaccount.google.com
                        </a>
                      </li>
                      <li>
                        Navigate to <strong>Security</strong> →{" "}
                        <strong>2-Step Verification</strong> (must be enabled)
                      </li>
                      <li>
                        Scroll down and click on <strong>App passwords</strong>
                      </li>
                      <li>
                        Select "Mail" and "Other (Custom name)" → Enter
                        "Botibonga"
                      </li>
                      <li>
                        Click <strong>Generate</strong> and copy the
                        16-character password
                      </li>
                    </ol>
                    <p className="text-xs text-blue-600 mt-3">
                      Note: App passwords only work if 2-Step Verification is
                      enabled on your Google account.
                    </p>
                  </div>
                </div>
              </div>

              {/* Gmail Address */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Gmail Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input
                    type="email"
                    placeholder="yourbusiness@gmail.com"
                    value={emailConfigTemp.gmail_address}
                    onChange={(e) =>
                      setEmailConfigTemp((prev) => ({
                        ...prev,
                        gmail_address: e.target.value,
                      }))
                    }
                    className="w-full pl-10 pr-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                </div>
                <p className="text-xs text-text-muted">
                  The Gmail address you want to send emails from. This must be a
                  Gmail account.
                </p>
              </div>

              {/* App Password */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  App Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="xxxx xxxx xxxx xxxx"
                    value={emailConfigTemp.app_password}
                    onChange={(e) =>
                      setEmailConfigTemp((prev) => ({
                        ...prev,
                        app_password: e.target.value.replace(/\s/g, ""),
                      }))
                    }
                    className="w-full px-4 py-3 pr-12 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5 text-text-muted" />
                    ) : (
                      <Eye className="w-5 h-5 text-text-muted" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-text-muted">
                  The 16-character App Password generated from your Google
                  Account. This is NOT your regular Gmail password.
                </p>
              </div>

              {/* Sender Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Sender Display Name{" "}
                  <span className="text-text-muted font-normal">
                    (Optional)
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Your Business Name"
                  value={emailConfigTemp.sender_name}
                  onChange={(e) =>
                    setEmailConfigTemp((prev) => ({
                      ...prev,
                      sender_name: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
                <p className="text-xs text-text-muted">
                  The name that appears in the "From" field. If left empty, your
                  Gmail address will be shown.
                </p>
              </div>

              {/* Signature */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-primary">
                  Email Signature{" "}
                  <span className="text-text-muted font-normal">
                    (Optional)
                  </span>
                </label>
                <textarea
                  placeholder={
                    "Best regards,\nThe [Your Business] Team\n\nPhone: +1 234 567 890\nWebsite: www.yourbusiness.com"
                  }
                  value={emailConfigTemp.signature}
                  onChange={(e) =>
                    setEmailConfigTemp((prev) => ({
                      ...prev,
                      signature: e.target.value,
                    }))
                  }
                  rows={4}
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                />
                <p className="text-xs text-text-muted">
                  This signature will be automatically added to the end of all
                  emails sent by your AI assistant.
                </p>
              </div>

              {/* Connect OAuth Coming Soon */}
              <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white border border-border flex items-center justify-center shadow-sm">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        Connect with Google OAuth
                      </p>
                      <p className="text-xs text-text-muted">
                        One-click sign in without App Password
                      </p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                    Coming Soon
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button
                onClick={handleCloseEmailModal}
                className="px-4 py-2 bg-gray-100 text-text-secondary rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmailConfig}
                className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Enable Gmail Integration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
